import { useEffect, useRef, useState } from "react";

import { DocumentEditorContainerComponent } from "@syncfusion/ej2-react-documenteditor";
import "@syncfusion/ej2-base/styles/material.css";
import "@syncfusion/ej2-buttons/styles/material.css";
import "@syncfusion/ej2-inputs/styles/material.css";
import "@syncfusion/ej2-popups/styles/material.css";
import "@syncfusion/ej2-lists/styles/material.css";
import "@syncfusion/ej2-navigations/styles/material.css";
import "@syncfusion/ej2-splitbuttons/styles/material.css";
import "@syncfusion/ej2-dropdowns/styles/material.css";
import "@syncfusion/ej2-react-documenteditor/styles/material.css";
import { PDFDocument } from "pdf-lib";
import { getDocumentSigned } from "../../../../api/deal";

export default function DealDocument({ document, signed, envelopedId }) {
  const hasFetchedSignedDoc = useRef(false);
  const containerRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const htmlToSFDT = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const body = doc.body;

    const blocks = [];

    const processNode = (node) => {
      // const inlineStack = [];

      const walk = (node, parentFormat = {}) => {
        let format = { ...parentFormat };
        let inlines = [];

        switch (node.nodeType) {
          case Node.TEXT_NODE:
            if (node.textContent.trim() !== "" || /\S/.test(node.textContent)) {
              inlines.push({
                characterFormat: format,
                text: node.textContent,
              });
            }
            break;

          case Node.ELEMENT_NODE:
            if (node.tagName === "BR") {
              inlines.push({ text: "\n", characterFormat: {} });
              break;
            }

            if (node.tagName === "STRONG" || node.tagName === "B") {
              format.bold = true;
            } else if (node.tagName === "I" || node.tagName === "EM") {
              format.italic = true;
            }

            node.childNodes.forEach((child) => {
              inlines.push(...walk(child, format));
            });
            break;

          default:
            break;
        }

        return inlines;
      };

      const handleBlock = (el) => {
        let inlines = walk(el);
        blocks.push({
          paragraphFormat: {},
          characterFormat: {},
          inlines: inlines,
        });
      };

      // Handling block elements
      node.childNodes.forEach((child) => {
        if (child.tagName === "P" || child.tagName === "H3") {
          handleBlock(child);
        } else if (child.tagName === "UL" || child.tagName === "OL") {
          child.querySelectorAll("li").forEach((li) => {
            const bulletInline = {
              text: "\u2022 ",
              characterFormat: {},
            };
            let listInlines = walk(li);
            blocks.push({
              paragraphFormat: {},
              characterFormat: {},
              inlines: [bulletInline, ...listInlines],
            });
          });
        } else if (child.nodeType === Node.TEXT_NODE) {
          handleBlock(child);
        } else {
          // fallback
          handleBlock(child);
        }
      });
    };

    processNode(body);

    return {
      sections: [
        {
          blocks,
        },
      ],
    };
  };

  const plainTextToSFDT = (text) => ({
    sections: [
      {
        blocks: [
          {
            characterFormat: {},
            paragraphFormat: {},
            inlines: [
              {
                characterFormat: {},
                text: text,
              },
            ],
          },
        ],
      },
    ],
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const textToSFDT = (inputText) => {
    try {
      const parsed =
        typeof inputText === "string" ? JSON.parse(inputText) : inputText;

      if (parsed && parsed.sec) {
        return parsed;
      }
    } catch {
      /* empty */
    }

    const isHTML = /<\/?[a-z][\s\S]*>/i.test(inputText);

    if (isHTML) {
      return htmlToSFDT(inputText);
    } else {
      return plainTextToSFDT(inputText);
    }
  };

  useEffect(() => {
    if (!containerRef.current?.documentEditor) return;

    containerRef.current.documentEditor.pageOutline = "#E0E0E0";
    containerRef.current.documentEditor.acceptTab = true;
    containerRef.current.documentEditor.resize();

    containerRef.current.documentEditor.isReadOnly = true;

    const sfdtDocument = textToSFDT(document);
    containerRef.current.documentEditor.open(JSON.stringify(sfdtDocument));
  }, [document, textToSFDT]);

  useEffect(() => {
    const initialize = async () => {
      if (signed && !hasFetchedSignedDoc.current) {
        hasFetchedSignedDoc.current = true;

        const { data } = await getDocumentSigned(envelopedId);
        const pdfDoc = await PDFDocument.load(data);
        pdfDoc.setTitle("nda");

        const modifiedBytes = await pdfDoc.save();
        const blob = new Blob([modifiedBytes], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);

        return;
      }
    };

    initialize();
  }, [envelopedId, signed]);

  if (signed) {
    return (
      <>
        {pdfUrl ? (
          <div style={{ height: "100%" }}>
            <iframe
              type="application/pdf"
              src={pdfUrl}
              width="100%"
              height="580px"
              style={{ border: "1px solid #ccc" }}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              gap: "10px",
              marginTop: "100px",
            }}
          >
            {/*<label>Loading NDA</label>*/}
            <div style={{ width: "100px", height: "400px" }}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                <linearGradient id="a11">
                  <stop offset="0" stopColor="#113A7C" stopOpacity="0"></stop>
                  <stop offset="1" stopColor="#113A7C"></stop>
                </linearGradient>
                <circle
                  fill="none"
                  stroke="url(#a11)"
                  strokeWidth="15"
                  strokeLinecap="round"
                  strokeDasharray="0 44 0 44 0 44 0 44 0 360"
                  cx="100"
                  cy="100"
                  r="70"
                  transformOrigin="center"
                >
                  <animateTransform
                    type="rotate"
                    attributeName="transform"
                    calcMode="discrete"
                    dur="2"
                    values="360;324;288;252;216;180;144;108;72;36"
                    repeatCount="indefinite"
                  ></animateTransform>
                </circle>
              </svg>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <DocumentEditorContainerComponent
        ref={containerRef}
        id="DocumentEditor"
        serviceUrl="https://sync.nrdeal.com/api/documenteditor/" //'https://services.syncfusion.com/react/production/api/documenteditor/'
        height="70vh"
      />
    </>
  );
}
